// src/components/ProductCard.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Star, Check, Eye } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import QuickViewModal from './QuickViewModal';

const ProductCard = ({ product }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [added, setAdded] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();

  const isWishlisted = isInWishlist(product.id);

  const images = product.images && product.images.length > 0
    ? product.images
    : (product.image ? [product.image] : ['https://via.placeholder.com/500']);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const discountPercent = product.originalPrice && product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex(prev => (prev + 1) % images.length);
      }, 1000);
      handleMouseEnter.interval = interval;
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (handleMouseEnter.interval) {
      clearInterval(handleMouseEnter.interval);
      handleMouseEnter.interval = null;
    }
    setCurrentImageIndex(0);
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product);
  };

  // ✅ Quick View Handler
  const handleQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowQuickView(true);
  };

  return (
    <>
      <div
        className="rounded-xl shadow-md overflow-hidden group transition-all duration-300 hover:shadow-xl hover:-translate-y-1 relative"
        style={{
          backgroundColor: 'var(--color-card-bg)',
          border: `1px solid var(--color-border)`,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >

        {/* Discount Badge */}
        {discountPercent > 0 && (
          <div
            className="absolute top-3 left-3 text-white text-xs font-bold px-2 py-1 rounded-full z-10"
            style={{ backgroundColor: '#DC829A' }}
          >
            -{discountPercent}%
          </div>
        )}

        {/* Featured Badge */}
        {product.featured && (
          <div
            className="absolute top-3 left-3 text-white text-xs font-bold px-2 py-1 rounded-full z-10 flex items-center gap-1"
            style={{ marginTop: discountPercent > 0 ? '30px' : '0', backgroundColor: '#F5AEC0' }}
          >
            <Star size={10} className="fill-white" /> Featured
          </div>
        )}

        {/* Wishlist */}
        <button
          onClick={handleWishlistClick}
          className="absolute top-3 right-3 rounded-full p-2 shadow-md z-10 hover:scale-110 transition-transform"
          style={{ backgroundColor: 'var(--color-card-bg)' }}
          title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <Heart
            size={18}
            className={isWishlisted ? 'fill-pink-500 text-pink-500 anim-heartBeat' : ''}
            style={!isWishlisted ? { color: '#DC829A' } : {}}
          />
        </button>

        {/* ✅ Quick View Button (always visible on hover) */}
        <button
          onClick={handleQuickView}
          className={`absolute top-14 right-3 rounded-full p-2 shadow-md z-10 hover:scale-110 transition-all duration-300 ${
            isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 md:opacity-0'
          }`}
          style={{ backgroundColor: 'var(--color-card-bg)' }}
          title="Quick View"
        >
          <Eye size={18} style={{ color: '#DC829A' }} />
        </button>

        {/* Image */}
        <Link to={`/product/${product.id}`}>
          <div className="relative overflow-hidden" style={{ backgroundColor: '#FFF2F6' }}>
            <img
              src={images[currentImageIndex]}
              alt={product.title}
              className={`w-full h-48 md:h-56 object-cover transition-transform duration-500 ${
                isHovered ? 'scale-110' : 'scale-100'
              }`}
            />

            {images.length > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                {images.map((_, i) => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      i === currentImageIndex ? 'bg-white w-4' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Quick Add (Desktop) */}
            <div
              className={`hidden md:block absolute bottom-0 left-0 right-0 py-2 text-center text-sm font-semibold transition-transform duration-300 ${
                isHovered ? 'translate-y-0' : 'translate-y-full'
              }`}
              style={{ backgroundColor: '#F5AEC0', color: '#FFFFFF' }}
            >
              <button
                onClick={handleAddToCart}
                className="flex items-center justify-center gap-2 w-full"
              >
                {added ? (
                  <><Check size={16} /> Added!</>
                ) : (
                  <><ShoppingCart size={16} /> Quick Add</>
                )}
              </button>
            </div>
          </div>
        </Link>

        {/* Details */}
        <div className="p-4">
          <p className="text-xs font-semibold uppercase mb-1" style={{ color: '#DC829A' }}>
            {product.category}
          </p>

          <Link to={`/product/${product.id}`}>
            <h3 className="text-base md:text-lg font-bold mb-2 transition line-clamp-2 hover:opacity-80"
              style={{ color: '#9B3F5B' }}>
              {product.title}
            </h3>
          </Link>

          <div className="flex items-center gap-1 mb-3">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={12}
                  className={i < Math.floor(product.rating || 5)
                    ? 'fill-pink-400 text-pink-400'
                    : 'text-gray-300'}
                />
              ))}
            </div>
            <span className="text-xs" style={{ color: '#DC829A' }}>
              ({product.rating || 5.0}) • {product.reviews || 0}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-lg md:text-xl font-bold" style={{ color: '#DC829A' }}>
                Rs. {product.price}
              </span>
              {product.originalPrice && product.originalPrice > product.price && (
                <span className="text-sm line-through ml-2" style={{ color: '#F5AEC0' }}>
                  Rs. {product.originalPrice}
                </span>
              )}
            </div>
          </div>

          {product.inventory !== undefined && (
            <p className={`text-xs mt-1 ${product.inventory > 0 ? 'text-pink-500' : 'text-red-500'}`}>
              {product.inventory > 0 ? `In Stock (${product.inventory})` : 'Out of Stock'}
            </p>
          )}

          {/* Mobile Buttons */}
          <div className="md:hidden flex gap-2 mt-3">
            <button
              onClick={handleAddToCart}
              className="flex-1 py-2 rounded-lg flex items-center justify-center gap-1 text-xs font-semibold btn-2d"
              style={{ backgroundColor: '#F5AEC0', color: '#FFFFFF' }}
            >
              {added ? (
                <><Check size={14} /> Added!</>
              ) : (
                <><ShoppingCart size={14} /> Add</>
              )}
            </button>
            <button
              onClick={handleQuickView}
              className="px-3 py-2 rounded-lg flex items-center justify-center gap-1 text-xs font-semibold border-2 btn-2d"
              style={{ borderColor: '#F5AEC0', color: '#DC829A', backgroundColor: 'transparent' }}
            >
              <Eye size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* ✅ Quick View Modal */}
      {showQuickView && (
        <QuickViewModal
          product={product}
          onClose={() => setShowQuickView(false)}
        />
      )}
    </>
  );
};

export default ProductCard;